import apiCoreAdapter from '../hooks/apiCoreAdapter';

export type SessionAvailability = 'available' | 'unavailable' | 'unknown';

/**
 * Verifica contra el back si una sesión sigue "viva" (no cancelada ni reprogramada).
 *
 * Una reprogramación cancela la sesión original y crea otra nueva, así que la
 * original deja de figurar entre los estados activos → se detecta como 'unavailable'.
 *
 * Consulta el listado del día (±1 día para cubrir bordes de zona horaria) filtrando
 * por los estados vivos (active, incomplete, settled) y busca la sesión por id.
 *
 * NO debe usarse con sesiones mock (status 'scheduled' o id temporal): todavía no
 * existen en la DB por la creación vía Make y siempre darían 'unavailable'.
 *
 * @returns
 *  - 'available'   → la sesión está entre las vivas del día.
 *  - 'unavailable' → el back trajo sesiones vivas del día pero esta no está.
 *  - 'unknown'     → no se pudo determinar (error de red/servidor o lista vacía);
 *                    el caller debería hacer fail-open y no bloquear acciones.
 */
export async function checkSessionAvailability(
  sessionId: string,
  dayISO: string,
): Promise<SessionAvailability> {
  const baseDay = (dayISO || '').split('T')[0];
  const [Y, M, D] = baseDay.split('-').map(Number);
  if (!Y || !M || !D) return 'unknown';

  const dayBound = (offset: number, end: boolean): string => {
    const d = new Date(Y, M - 1, D + offset);
    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return end ? `${s} 23:59:59` : `${s} 00:00:00`;
  };

  try {
    // Pedimos también las canceladas para poder detectar por el status real de la sesión
    // (si pidiéramos solo las vivas, una sesión cancelada que era la única del día daría
    // lista vacía y no podríamos distinguir "cancelada" de "error").
    const resp = await apiCoreAdapter.psychologistSessionsList({
      from: dayBound(-1, false),
      to: dayBound(1, true),
      take: 100,
      status: 'active,incomplete,settled,cancelled',
    });
    const list = Array.isArray(resp?.data) ? resp.data : [];

    const ALIVE = new Set(['active', 'incomplete', 'settled']);
    const found = list.find((s) => String(s.id) === String(sessionId));
    if (found) {
      // Encontrada: disponible solo si su estado sigue siendo "vivo".
      return ALIVE.has(String(found.status).toLowerCase()) ? 'available' : 'unavailable';
    }

    // No figura en el rango:
    // - lista vacía → o el adapter tragó un error o el día no tiene sesiones → fail-open.
    // - lista con sesiones pero esta no → la sesión ya no existe → no disponible.
    return list.length === 0 ? 'unknown' : 'unavailable';
  } catch {
    return 'unknown';
  }
}

/**
 * Indica si una sesión es "mock" (todavía no persistida en la DB) y por lo tanto
 * NO debe verificarse contra el back. Cubre el status optimista 'scheduled' y los
 * ids temporales con formato `YYYY-MM-DD-HH:MM-email`.
 */
export function isMockSession(sessionId: string, status?: string): boolean {
  const isTemporaryId =
    sessionId.includes('@') && /^\d{4}-\d{2}-\d{2}-\d{2}:\d{2}-/.test(sessionId);
  return status === 'scheduled' || isTemporaryId;
}
